import { c as _c } from "react/compiler-runtime";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";

// `default` stacks plain options, `cards` spaces selectable option cards, and
// `list` joins options into one bordered settings list with dividers.
const radioGroupVariants = cva("grid", {
  variants: {
    variant: {
      default: "gap-2",
      cards: "gap-3",
      list: "gap-0 divide-y divide-border-primary overflow-hidden rounded-settings-list-view-item border border-border-primary"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});

/** Use when a person picks exactly one of a few visible options. */
const RadioGroup = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let ref;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = variant;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    variant = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== variant) {
    t1 = cn(radioGroupVariants({
      variant
    }), className);
    $[5] = className;
    $[6] = variant;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = <RadioGroupPrimitive.Root className={t1} {...props} ref={ref} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

// Linear radio: 14×14 circle with the checkbox border, accent fill and a white
// dot when checked, 1px accent focus outline.
const RadioGroupItem = t0 => {
  const $ = _c(11);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex aspect-square size-3.5 shrink-0 items-center justify-center rounded-full", "border border-border-strong bg-bg-surface outline-none hover:border-text-tertiary", "focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent", "data-[state=checked]:border-control-checked data-[state=checked]:bg-control-checked", "disabled:cursor-not-allowed disabled:opacity-50", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RadioGroupPrimitive.Indicator className="block size-1.5 rounded-full bg-text-on-accent" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== ref || $[9] !== t1) {
    t3 = <RadioGroupPrimitive.Item ref={ref} className={t1} {...props}>{t2}</RadioGroupPrimitive.Item>;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// Selectable option card for `variant="cards"`: the radio, a title and an
// optional detail line inside one bordered surface that highlights when chosen.
const RadioGroupCard = t0 => {
  const $ = _c(15);
  const {
    value,
    title,
    description,
    disabled
  } = t0;
  const t1 = disabled || undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex cursor-pointer items-start gap-3 rounded-settings-list-view-item border border-border-primary bg-bg-surface p-4", "hover:border-border-strong has-[[data-state=checked]]:border-control-checked", "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-disabled data-[disabled]:hover:border-border-primary");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== disabled || $[2] !== value) {
    t3 = <RadioGroupItem className="mt-0.5" value={value} disabled={disabled} />;
    $[1] = disabled;
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== title) {
    t4 = <span className="text-base font-medium text-text-primary">{title}</span>;
    $[4] = title;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== description) {
    t5 = description && <span className="text-base text-text-tertiary">{description}</span>;
    $[6] = description;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <span className="flex min-w-0 flex-col gap-0.5">{t4}{t5}</span>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t6) {
    t7 = <label data-disabled={t1} className={t2}>{t3}{t6}</label>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
export { RadioGroup, RadioGroupCard, RadioGroupItem };